// src/pages/partners/Partners.tsx
//
// Partners page
// - Uses PartnersSearchPanel (shared SearchPanel wrapper).
// - Keeps URL query params in sync with the *applied* search state for shareable deep links.
// - Maintains draft vs applied search states.
// - Auto-search behavior is controlled by SearchPanel via `autoSearch` prop.
//
// Table refactor policy:
// - Replace legacy summary + go-to-page UI with StandardTableToolbar.
// - Use StandardTableFrame + StandardPagination.
// - Navigation rule: DO NOT navigate on row click. Navigate ONLY when clicking Index cell.
// - Preserve legacy Partner ID display formatting via toDisplayPartnerId.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
  Paper,
  TableCell,
  TableRow,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { usePartnerList } from '@queries/partners/usePartnerList';
import { usePartnerCount } from '@queries/partners/usePartnerCount';

import { filterSearchParams } from '@utils/search';
import { timestampToDate } from '@utils/time';
import { toUsdDisplay } from '@utils/money';
import { toDisplayPartnerId } from './util';

import type { PartnersSearchForm } from '@models/partner';

import { usePageHeader } from '@components/common/pageHeader.context';
import PartnersSearchPanel from '@components/partner/PartnersSearchPanel';
import type { SetSearchValueFn } from '@components/common/search';

import {
  StandardTableToolbar,
  StandardTableFrame,
  StandardPagination,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;

/**
 * Page-level policy:
 * - Global autoSearch default is OFF (SearchPanel constants).
 * - Enable autoSearch only for this page if desired.
 */
const PAGE_AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

const EMPTY_FORM: PartnersSearchForm = {
  name: '',
  manager: '',
  email: '',
  address: '',
};

const parsePositiveInt = (value: string | null, fallback = 1) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const readFormFromUrl = (sp: URLSearchParams): PartnersSearchForm => ({
  name: sp.get('name') || '',
  manager: sp.get('manager') || '',
  email: sp.get('email') || '',
  address: sp.get('address') || '',
});

/**
 * Normalize a search form:
 * - Trim strings
 * - Drop empty values
 */
const cleanSearchForm = (form: PartnersSearchForm): PartnersSearchForm => {
  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(form)) {
    if (value === undefined || value === null) continue;

    if (typeof value === 'string') {
      const v = value.trim();
      if (!v) continue;
      cleaned[key] = v;
      continue;
    }

    cleaned[key] = value;
  }

  return cleaned as PartnersSearchForm;
};

const buildUrlParams = (form: PartnersSearchForm, page: number) => {
  const cleaned = cleanSearchForm(form);
  const stringParams: Record<string, string> = {};

  for (const [k, v] of Object.entries(cleaned)) {
    stringParams[k] = String(v);
  }

  return {
    ...filterSearchParams(stringParams),
    page: String(page),
  };
};

const formsEqual = (a: PartnersSearchForm, b: PartnersSearchForm) =>
  (a.name ?? '') === (b.name ?? '') &&
  (a.manager ?? '') === (b.manager ?? '') &&
  (a.email ?? '') === (b.email ?? '') &&
  (a.address ?? '') === (b.address ?? '');

const Partners = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /* ------------------------------------------------------------------ */
  /* Page header slots                                                   */
  /* ------------------------------------------------------------------ */

  const { setPageHeaderSlots } = usePageHeader();

  const handleAddPartner = useCallback(() => {
    navigate('/partners/add');
  }, [navigate]);

  useEffect(() => {
    setPageHeaderSlots({
      actions: [
        {
          key: 'add-partner',
          label: 'Add Partner',
          startIcon: <Add />,
          onClick: handleAddPartner,
          primary: true,
          variant: 'contained',
          color: 'primary',
        },
      ],
    });

    // Clear header slots on unmount to avoid leaking actions into other pages.
    return () => setPageHeaderSlots({ actions: [] });
  }, [handleAddPartner, setPageHeaderSlots]);

  /* ------------------------------------------------------------------ */
  /* Search state (draft vs applied)                                     */
  /* ------------------------------------------------------------------ */

  const [draftSearch, setDraftSearch] = useState<PartnersSearchForm>(() => readFormFromUrl(searchParams));
  const [appliedSearch, setAppliedSearch] = useState<PartnersSearchForm>(() =>
    cleanSearchForm(readFormFromUrl(searchParams)),
  );

  /* ------------------------------------------------------------------ */
  /* Pagination                                                         */
  /* ------------------------------------------------------------------ */

  const [page, setPage] = useState<number>(() => parsePositiveInt(searchParams.get('page'), 1));

  // Filter panel accordion open state (controlled).
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [includeDeleted, setIncludeDeleted] = useState(false);

  /* ------------------------------------------------------------------ */
  /* Sync local state from URL (back/forward / deep links)               */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const nextDraft = readFormFromUrl(searchParams);
    const nextApplied = cleanSearchForm(nextDraft);
    const nextPage = parsePositiveInt(searchParams.get('page'), 1);

    setDraftSearch((prev) => (formsEqual(prev, nextDraft) ? prev : nextDraft));
    setAppliedSearch((prev) => (formsEqual(prev, nextApplied) ? prev : nextApplied));
    setPage((prev) => (prev === nextPage ? prev : nextPage));
  }, [searchParams]);

  /* ------------------------------------------------------------------ */
  /* URL sync helper (based on applied)                                  */
  /* ------------------------------------------------------------------ */

  const syncUrlFromApplied = useCallback(
    (nextApplied: PartnersSearchForm, nextPage: number) => {
      setSearchParams(buildUrlParams(nextApplied, nextPage));
    },
    [setSearchParams],
  );

  /* ------------------------------------------------------------------ */
  /* Draft setter used by inputs/chips                                   */
  /* ------------------------------------------------------------------ */

  const setValue: SetSearchValueFn<PartnersSearchForm> = useCallback((key, value) => {
    // Keep empty strings in draft; normalize at apply time.
    setDraftSearch((prev) => ({
      ...prev,
      [key]: ((value ?? '') as unknown) as PartnersSearchForm[keyof PartnersSearchForm],
    }));
  }, []);

  /* ------------------------------------------------------------------ */
  /* Apply / Reset                                                       */
  /* ------------------------------------------------------------------ */

  const handleSearch = useCallback(() => {
    const nextApplied = cleanSearchForm(draftSearch);

    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [draftSearch, syncUrlFromApplied]);

  const handleReset = useCallback(() => {
    setDraftSearch(EMPTY_FORM);

    const nextApplied = cleanSearchForm(EMPTY_FORM);

    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [syncUrlFromApplied]);

  /* ------------------------------------------------------------------ */
  /* Queries (MUST use applied, not draft)                               */
  /* ------------------------------------------------------------------ */

  const cleanedApplied = useMemo(() => cleanSearchForm(appliedSearch), [appliedSearch]);

  const { data: listData, isLoading: isLoadingList } = usePartnerList({
    page,
    pageSize: PAGE_SIZE,
    searchParam: cleanedApplied,
    includeDeleted,
  });

  const { data: totalCount, isLoading: isLoadingCount } = usePartnerCount(
    cleanedApplied,
    includeDeleted,
  );

  const rows = useMemo(() => listData?.data ?? [], [listData]);

  const safeTotalCount = totalCount ?? 0;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(safeTotalCount / PAGE_SIZE)), [safeTotalCount]);

  // Safety: clamp current page if count changes.
  useEffect(() => {
    setPage((prev) => clamp(prev, 1, totalPages));
  }, [totalPages]);

  const isTableLoading = Boolean(isLoadingList || isLoadingCount);

  /* ------------------------------------------------------------------ */
  /* Paging handlers                                                     */
  /* ------------------------------------------------------------------ */

  const handleChangePage = (next: number) => {
    const value = clamp(next, 1, totalPages);
    setPage(value);
    syncUrlFromApplied(appliedSearch, value);
  };

  /* ------------------------------------------------------------------ */
  /* Table                                                               */
  /* ------------------------------------------------------------------ */

  const columnsCount = 10;

  const handleNavigateDetail = useCallback(
    (partnerId: number) => {
      if (!Number.isFinite(partnerId) || partnerId <= 0) return;
      navigate(`/partners/${partnerId}`);
    },
    [navigate],
  );

  return (
    <Box sx={{ minWidth: 0 }}>
      <PartnersSearchPanel
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        searchForm={draftSearch}
        setValue={setValue}
        onSearch={handleSearch}
        onReset={handleReset}
        autoSearch={PAGE_AUTO_SEARCH}
      />

      {/* Include Deleted toggle */}
      <Box mt={1}>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeDeleted}
              onChange={(e) => {
                setIncludeDeleted(e.target.checked);
                setPage(1);
              }}
              size="small"
            />
          }
          label="Include Deleted"
        />
      </Box>

      {/* Standard toolbar (Total + Page Jump) */}
      <Box mt={2} />
      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={safeTotalCount}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      <Box mt={1}>
        <Paper
          sx={(theme) => ({
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          })}
        >
          {isTableLoading ? <LinearProgress /> : null}

          <StandardTableFrame<any>
            head={
              <TableRow>
                <TableCell width={70} align="right">
                  #
                </TableCell>
                <TableCell width={140}>Partner ID</TableCell>
                <TableCell>Partner Name</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Balance (USD)</TableCell>
                <TableCell>Account Manager</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
                <TableCell align="center">Created</TableCell>
              </TableRow>
            }
            rows={rows}
            getRowKey={(row) => String(row.partnerId)}
            columnsCount={columnsCount}
            isLoading={isTableLoading}
            loadingVariant="skeleton"
            skeletonRows={8}
            emptyText="No partners found."
            minWidth={1200}
            renderRow={(row, index) => {
              const partnerId = Number(row.partnerId);
              const canNavigate = Number.isFinite(partnerId) && partnerId > 0;
              const isDeleted = row.status === 'DELETED';

              return (
                <React.Fragment>
                  {/* Index-only navigation */}
                  <TableCell
                    align="right"
                    sx={{
                      ...cellSx.number,
                      cursor: canNavigate ? 'pointer' : 'default',
                      textDecoration: canNavigate ? 'underline' : 'none',
                      textUnderlineOffset: canNavigate ? '3px' : undefined,
                      '&:hover': canNavigate ? { opacity: 0.85 } : undefined,
                      opacity: isDeleted ? 0.5 : 1,
                    }}
                    onClick={() => {
                      if (!canNavigate) return;
                      handleNavigateDetail(partnerId);
                    }}
                  >
                    {(page - 1) * PAGE_SIZE + index + 1}
                  </TableCell>

                  {/* Preserve legacy Partner ID display format */}
                  <TableCell sx={{ whiteSpace: 'nowrap', opacity: isDeleted ? 0.5 : 1 }}>
                    {canNavigate ? toDisplayPartnerId(partnerId) : '-'}
                  </TableCell>

                  <TableCell sx={{ opacity: isDeleted ? 0.5 : 1 }}>{row.name ?? '-'}</TableCell>

                  <TableCell align="center">
                    {row.status === 'INACTIVE' && (
                      <Chip label="Inactive" color="warning" size="small" />
                    )}
                    {row.status === 'DELETED' && (
                      <Chip label="Deleted" color="default" size="small" />
                    )}
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{ whiteSpace: 'nowrap', opacity: isDeleted ? 0.5 : 1 }}
                  >
                    {toUsdDisplay(row.balance)}
                  </TableCell>

                  <TableCell sx={{ opacity: isDeleted ? 0.5 : 1 }}>{row.managerName ?? '-'}</TableCell>
                  <TableCell sx={{ opacity: isDeleted ? 0.5 : 1 }}>{row.managerEmail ?? '-'}</TableCell>
                  <TableCell sx={{ opacity: isDeleted ? 0.5 : 1 }}>{row.managerTelephone ?? '-'}</TableCell>
                  <TableCell sx={{ minWidth: 220, opacity: isDeleted ? 0.5 : 1 }}>
                    {row.address ?? '-'}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{ whiteSpace: 'nowrap', opacity: isDeleted ? 0.5 : 1 }}
                  >
                    {timestampToDate(row.createdAt)}
                  </TableCell>
                </React.Fragment>
              );
            }}
          />
        </Paper>
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={safeTotalCount}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default Partners;
